import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { switchcase } from '@sweetgreen/garnish';

import type { useCustomer } from '@order/Customer';
import type { useCart } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import type { TelemetryEvent } from '@order/Telemetry';
import { useTelemetry } from '@order/Telemetry';

import { useOrderStatusTelemetry } from '../../../OrderStatusScreen/OrderStatus.telemetry';
import { useCheckoutContext } from '../../CheckoutProvider';

export const useCheckoutTelemetry = ({
  customer,
  cart,
  cartOrderType: orderType,
  total,
}: CheckoutTelemetryProps) => {
  // Cart
  const orderId = cart?.id;
  const restaurantId = cart?.restaurant?.id;
  const restaurantName = cart?.restaurant?.name;

  // Line Items
  const productIds = React.useMemo(
    () =>
      cart?.lineItems.map(({ product }) => product?.id ?? '')?.filter(Boolean),
    [cart?.lineItems],
  );
  const productNames = React.useMemo(
    () =>
      cart?.lineItems
        .map(({ product }) => product?.name ?? '')
        ?.filter(Boolean),
    [cart?.lineItems],
  );
  const bagCount = React.useMemo(
    () =>
      cart?.lineItems
        .map(({ quantity }) => quantity ?? 0)
        .reduce((count: number, quantity: number) => count + quantity, 0),
    [cart?.lineItems],
  );

  // Restaurant Address
  const restaurantAddress = React.useMemo(
    () => ({
      street: cart?.restaurant?.address ?? '',
      city: cart?.restaurant?.city ?? '',
      state: cart?.restaurant?.state ?? '',
      zipCode: cart?.restaurant?.zipCode ?? '',
      country: 'US',
    }),
    [
      cart?.restaurant?.address,
      cart?.restaurant?.city,
      cart?.restaurant?.state,
      cart?.restaurant?.zipCode,
    ],
  );

  // Delivery Order Detail
  const deliveryOrderDetail = React.useMemo(
    () => ({
      street: cart?.deliveryOrderDetail?.address?.street ?? '',
      city: cart?.deliveryOrderDetail?.address?.city ?? '',
      state: cart?.deliveryOrderDetail?.address?.state ?? '',
      zipCode: cart?.deliveryOrderDetail?.address?.zipCode ?? '',
      country: cart?.deliveryOrderDetail?.address?.country ?? 'US',
    }),
    [
      cart?.deliveryOrderDetail?.address?.city,
      cart?.deliveryOrderDetail?.address?.country,
      cart?.deliveryOrderDetail?.address?.state,
      cart?.deliveryOrderDetail?.address?.street,
      cart?.deliveryOrderDetail?.address?.zipCode,
    ],
  );

  // Customer
  const customerTelemetry = React.useMemo(
    () => ({
      userId: customer?.id ?? '',
      firstName: customer?.firstName ?? '',
      lastName: customer?.lastName ?? '',
      email: customer?.email ?? '',
      phoneNumber: customer?.phoneNumber ?? '',
    }),
    [
      customer?.id,
      customer?.email,
      customer?.firstName,
      customer?.lastName,
      customer?.phoneNumber,
    ],
  );

  const data: TelemetryEvent<'checkout.view'>['payload'] = React.useMemo(
    () => ({
      orderId,
      restaurantId,
      restaurantName,
      orderType,
      productIds,
      productNames,
      bagCount,
      orderValue: Number(((total ?? 0) / 100).toFixed(2)),
      orderTotal: Number(((total ?? 0) / 100).toFixed(2)),
      customer: customerTelemetry,
      isDelivery: orderType === 'delivery',
      ...(orderType === 'delivery' ? deliveryOrderDetail : restaurantAddress),
    }),
    [
      orderId,
      restaurantId,
      restaurantName,
      orderType,
      productIds,
      productNames,
      bagCount,
      total,
      customerTelemetry,
      deliveryOrderDetail,
      restaurantAddress,
    ],
  );

  const checkoutViewEventRef = React.useRef<TelemetryEvent<'checkout.view'>>();
  const checkoutViewEvent: TelemetryEvent<'checkout.view'> = React.useMemo(
    () => ({
      name: 'checkout.view',
      payload: data,
    }),
    [data],
  );

  // eslint-disable-next-line functional/immutable-data
  checkoutViewEventRef.current = checkoutViewEvent;

  const checkoutStartEvent: TelemetryEvent<'checkout.start'> = React.useMemo(
    () => ({
      name: 'checkout.start',
      payload: data,
    }),
    [data],
  );

  const checkoutSuccessEvent: TelemetryEvent<'checkout.success'> =
    React.useMemo(
      () => ({
        name: 'checkout.success',
        payload: data,
      }),
      [data],
    );

  return {
    data,
    checkoutViewEvent: checkoutViewEventRef,
    checkoutStartEvent,
    checkoutSuccessEvent,
  };
};

export const useCheckoutTelemetryEvents = (
  checkoutTelemetryProps: Parameters<typeof useCheckoutTelemetry>[0],
) => {
  const { track, trackEvent } = useTelemetry();
  const { t } = useLocalizationContext();
  const { handleOrderStatusEvent } = useOrderStatusTelemetry();
  const { checkoutStartEvent, checkoutSuccessEvent } = useCheckoutTelemetry(
    checkoutTelemetryProps,
  );

  const checkoutStart = useCallback(() => {
    trackEvent(checkoutStartEvent);
  }, [checkoutStartEvent, trackEvent]);

  const checkoutSuccess = useCallback(() => {
    handleOrderStatusEvent('checkout.success');
    trackEvent(checkoutSuccessEvent);
  }, [checkoutSuccessEvent, handleOrderStatusEvent, trackEvent]);

  const timeslotUnavailable = useCallback(
    (systemError: string) => {
      track('checkout.failure', {
        userError: t('checkout.timeslot-unavailable'),
        systemError,
      });
    },
    [t, track],
  );

  const deliveryMinimumNotMet = useCallback(
    (systemError: string, minimumDeliveryAmount: string) => {
      track('checkout.failure', {
        userError: t('bag.delivery-minimum', {
          minimum: minimumDeliveryAmount,
        }),
        systemError,
      });
    },
    [t, track],
  );

  const productUnavailable = useCallback(
    (systemError: string, errorMessage: string) => {
      track('checkout.failure', {
        userError: errorMessage,
        systemError,
      });
    },
    [track],
  );

  const restaurantIsNotAcceptingOrders = useCallback(
    (systemError: string, errorMessage: string) => {
      track('checkout.failure', {
        userError: errorMessage,
        systemError,
      });
    },
    [track],
  );

  const otherKnownErrors = useCallback(
    (systemError: string, typename: string) => {
      track('checkout.failure', {
        userError: switchcase({
          InvalidBillingAccount: t('invalid-billing-account'),
          InvalidCustomerPhone: t('invalid-customer-phone'),
          DeliveryAlreadyInFlight: t('delivery-in-flight'),
          DeliverySpecifyAddress: t('delivery-specify-address'),
        })(t('general.error'))(typename),
        systemError,
      });
    },
    [t, track],
  );

  const unknownCheckoutError = useCallback(
    (systemError: string) => {
      track('checkout.failure', { userError: t('general.error'), systemError });
    },
    [t, track],
  );

  return useMemo(
    () => ({
      checkoutStart,
      checkoutSuccess,
      timeslotUnavailable,
      deliveryMinimumNotMet,
      productUnavailable,
      restaurantIsNotAcceptingOrders,
      otherKnownErrors,
      unknownCheckoutError,
    }),
    [
      checkoutStart,
      checkoutSuccess,
      timeslotUnavailable,
      deliveryMinimumNotMet,
      productUnavailable,
      restaurantIsNotAcceptingOrders,
      otherKnownErrors,
      unknownCheckoutError,
    ],
  );
};

export const useCheckoutViewEventEffect = (
  props: CheckoutViewEventEffectProps & CheckoutTelemetryProps,
) => {
  const {
    customer,
    cart,
    cartOrderType,
    total,
    isCustomerDataReady,
    isLoadingCheckout,
  } = props;

  const { trackEvent } = useTelemetry();

  const { checkoutViewEvent } = useCheckoutTelemetry({
    customer,
    cart,
    cartOrderType,
    total,
  });

  useLayoutEffect(() => {
    if (
      isCustomerDataReady &&
      !isLoadingCheckout &&
      checkoutViewEvent.current
    ) {
      trackEvent(checkoutViewEvent.current);
    }
  }, [checkoutViewEvent, isCustomerDataReady, isLoadingCheckout, trackEvent]);
};

export const useConnectedCheckoutViewEventEffect = (
  props: CheckoutViewEventEffectProps,
) => {
  const { isCustomerDataReady, isLoadingCheckout } = props;
  const { customer, cart, total, cartOrderType } = useCheckoutContext();

  useCheckoutViewEventEffect({
    customer,
    cart,
    cartOrderType,
    total,
    isCustomerDataReady,
    isLoadingCheckout,
  });
};

type CheckoutViewEventEffectProps = {
  readonly isCustomerDataReady: boolean;
  readonly isLoadingCheckout: boolean;
};

type CheckoutTelemetryProps = {
  readonly customer: ReturnType<typeof useCustomer>['customer'];
  readonly cart: ReturnType<typeof useCart>['cart'];
  readonly cartOrderType: ReturnType<typeof useCart>['cartOrderType'];
  readonly total: ReturnType<typeof useCart>['total'];
};
